import { Component, LOCALE_ID, computed, inject, input } from '@angular/core';
import { CommonModule, formatDate } from '@angular/common';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ChartModule } from 'primeng/chart';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { SignedSrcDirective } from '../../../shared/directives/signed-src.directive';
import { appointmentStatusKey } from '../../../core/utils/appointment-status.utils';
import { StaffDashboard } from '../../../core/models/clinic-dashboard.model';
import { MODULE, PermissionService } from '../../../core/services/permission.service';
import { DashStatCard } from './stat-card';

@Component({
  selector: 'app-dash-staff-view',
  standalone: true,
  imports: [CommonModule, TranslateModule, ChartModule, SignedSrcDirective, DashStatCard],
  template: `
    <div class="space-y-6">
      <!-- Agenda -->
      <div *ngIf="agenda() && data().schedule as sc" class="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <app-dash-stat-card [label]="'dash_to_arrive' | translate" [value]="sc.to_arrive" icon="pi-calendar" />
        <app-dash-stat-card [label]="'dash_arrived' | translate" [value]="sc.arrived" icon="pi-sign-in" valueClass="text-amber-600" />
        <app-dash-stat-card [label]="'dash_in_service' | translate" [value]="sc.in_service" icon="pi-heart" valueClass="text-primary" />
        <app-dash-stat-card [label]="'dash_done' | translate" [value]="sc.done" icon="pi-check-circle" valueClass="text-emerald-600" />
        <app-dash-stat-card [label]="'dash_no_show' | translate" [value]="sc.no_show" icon="pi-times-circle" valueClass="text-red-600" />
      </div>

      <div *ngIf="smallCards() as n" class="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <ng-container *ngIf="agenda() && data().today_extras as ex">
          <app-dash-stat-card [label]="'dash_tomorrow_appointments' | translate" [value]="data().tomorrow_total" icon="pi-calendar-plus" />
          <app-dash-stat-card [label]="'dash_cancelled_today' | translate" [value]="ex.cancelled" icon="pi-ban" valueClass="text-red-600" />
          <app-dash-stat-card [label]="'dash_rescheduled_today' | translate" [value]="ex.rescheduled" icon="pi-replay" valueClass="text-amber-600" />
        </ng-container>
        <app-dash-stat-card *ngIf="patients() && data().new_patients_today !== undefined" [label]="'dash_new_patients_today' | translate"
          [value]="data().new_patients_today" icon="pi-user-plus" />
      </div>

      <div *ngIf="agenda() && data().next_arrivals" class="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <!-- Next arrivals -->
        <div class="lg:col-span-2 bg-white rounded-3xl p-4 sm:p-6 shadow-sm border border-slate-100">
          <p class="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-4">{{ 'dash_next_arrivals' | translate }}</p>
          <p *ngIf="!data().next_arrivals!.length" class="text-sm text-slate-400 py-10 text-center">{{ 'dash_nothing_upcoming' | translate }}</p>
          <ul class="divide-y divide-slate-100 max-h-80 overflow-y-auto pr-2 -mr-2">
            <li *ngFor="let a of data().next_arrivals" class="flex items-center gap-2 sm:gap-3 py-3">
              <span class="w-11 sm:w-14 shrink-0 text-xs sm:text-sm font-black text-slate-900">{{ a.time_start }}</span>
              <img [appSignedSrc]="a.patient_photo" bucket="patient_documents" class="w-8 h-8 sm:w-9 sm:h-9 shrink-0 rounded-full border border-slate-100 object-cover" alt="">
              <div class="flex-1 min-w-0">
                <p class="text-sm font-bold text-slate-800 truncate">{{ a.patient_name || '-' }}</p>
                <p class="text-xs text-slate-400 truncate">{{ a.doctor_name }}</p>
              </div>
              <span class="text-xs font-bold px-2.5 py-1 rounded-full whitespace-nowrap shrink-0"
                [ngStyle]="{ background: a.color_bg || '#f1f5f9', color: a.color_text || '#475569' }">
                {{ statusKey(a.status_name) | translate }}
              </span>
            </li>
          </ul>
        </div>

        <!-- Confirmation status: next 7 days -->
        <div *ngIf="confirmationChart() as c" class="bg-white rounded-3xl p-4 sm:p-6 shadow-sm border border-slate-100 flex flex-col">
          <p class="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-4">{{ 'dash_confirmation' | translate }} · {{ 'dash_next_7_days' | translate }}</p>
          <div class="relative flex-1 min-h-[300px]">
            <p-chart type="bar" [data]="c" [options]="confirmationOptions" [plugins]="plugins" width="100%" height="100%" />
          </div>
        </div>
      </div>

      <div *ngIf="lowerCards()" class="grid grid-cols-1 gap-6 items-stretch" [ngClass]="agenda() && data().doctor_load ? 'lg:grid-cols-2' : ''">
        <div class="flex flex-col gap-6">
          <!-- Billing backlog (counts only) -->
          <div *ngIf="finance() && data().billing as b" class="bg-white rounded-3xl p-4 sm:p-6 shadow-sm border border-slate-100">
            <p class="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-4">{{ 'dash_billing_pending' | translate }}</p>
            <div class="grid grid-cols-2 gap-4">
              <div class="rounded-2xl bg-amber-50 p-4">
                <p class="text-3xl font-black text-amber-600">{{ b.to_invoice }}</p>
                <p class="text-xs font-semibold text-slate-500 mt-1">{{ 'dash_to_invoice' | translate }}</p>
              </div>
              <div class="rounded-2xl bg-primary/10 p-4">
                <p class="text-3xl font-black text-primary">{{ b.awaiting_payment }}</p>
                <p class="text-xs font-semibold text-slate-500 mt-1">{{ 'dash_awaiting_payment' | translate }}</p>
              </div>
            </div>
            <p class="text-[11px] text-slate-400 mt-3">{{ 'dash_billing_note' | translate }}</p>
          </div>

          <!-- Waitlist -->
          <div *ngIf="agenda() && data().waitlist as w" class="bg-white rounded-3xl p-4 sm:p-6 shadow-sm border border-slate-100">
            <div class="flex items-center justify-between mb-4">
              <p class="text-[10px] font-black text-slate-400 uppercase tracking-widest">{{ 'dash_waitlist' | translate }}</p>
              <span class="text-lg font-black text-slate-900">{{ w.count }}</span>
            </div>
            <p *ngIf="!w.top.length" class="text-sm text-slate-400 py-4 text-center">{{ 'dash_waitlist_empty' | translate }}</p>
            <ul class="space-y-2">
              <li *ngFor="let item of w.top" class="flex items-center justify-between gap-3 text-sm">
                <span class="min-w-0 font-bold text-slate-800 truncate">{{ item.patient_name || '-' }}</span>
                <span class="max-w-[45%] shrink-0 text-xs text-slate-400 truncate">{{ item.doctor_name }}</span>
              </li>
            </ul>
          </div>
        </div>

        <!-- Doctor load -->
        <div *ngIf="agenda() && data().doctor_load as load" class="bg-white rounded-3xl p-4 sm:p-6 shadow-sm border border-slate-100 flex flex-col">
          <p class="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-4">{{ 'dash_doctor_load' | translate }}</p>
          <p *ngIf="!load.length" class="text-sm text-slate-400 py-6 text-center">{{ 'dash_nothing_today' | translate }}</p>
          <ul class="space-y-3 flex-1 min-h-0 max-h-80 overflow-y-auto pr-2 -mr-2">
            <li *ngFor="let d of load" class="flex items-center gap-3">
              <span class="w-9 h-9 rounded-full bg-primary/10 text-primary flex items-center justify-center text-sm font-black">{{ d.name.charAt(0) }}</span>
              <div class="flex-1 min-w-0">
                <p class="text-sm font-bold text-slate-900 truncate">{{ d.name }}</p>
                <div class="h-1.5 rounded-full bg-slate-100 mt-1.5 overflow-hidden">
                  <div class="h-full bg-emerald-500" [style.width.%]="d.total ? (d.done / d.total) * 100 : 0"></div>
                </div>
              </div>
              <span class="text-sm font-black text-slate-700">{{ d.done }}/{{ d.total }}</span>
            </li>
          </ul>
        </div>
      </div>

      <div *ngIf="!anything()" class="bg-white rounded-3xl p-8 shadow-sm border border-slate-100 text-center text-slate-500">
        {{ 'dash_no_widgets' | translate }}
      </div>
    </div>
  `,
})
export class DashStaffView {
  data = input.required<StaffDashboard>();
  readonly statusKey = appointmentStatusKey;

  private perm = inject(PermissionService);
  private translate = inject(TranslateService);
  private locale = inject(LOCALE_ID);
  readonly plugins = [ChartDataLabels];

  readonly confirmationChart = computed(() => {
    const c = this.data().confirmation;
    if (!c) return null;
    const t = (k: string) => this.translate.instant(k);
    return {
      labels: c.map((d, i) => (i === 0 ? t('dash_today') : formatDate(d.day, 'EEE d', this.locale))),
      datasets: [
        { label: t('dash_confirmed'), data: c.map(d => d.confirmed), backgroundColor: '#10b981', borderRadius: 6, borderSkipped: false, maxBarThickness: 22 },
        { label: t('dash_unconfirmed'), data: c.map(d => d.unconfirmed), backgroundColor: '#f59e0b', borderRadius: 6, borderSkipped: false, maxBarThickness: 22 },
      ],
    };
  });

  readonly confirmationOptions = {
    indexAxis: 'y' as const,
    maintainAspectRatio: false,
    animation: { duration: 900, easing: 'easeOutQuart' as const },
    plugins: {
      legend: { position: 'bottom' as const, labels: { usePointStyle: true, boxWidth: 8, color: '#64748b' } },
      tooltip: { displayColors: true },
      datalabels: {
        color: '#fff',
        font: { weight: 'bold' as const, size: 12 },
        display: (ctx: any) => ctx.dataset.data[ctx.dataIndex] > 0,
      },
    },
    scales: {
      x: { stacked: true, display: false },
      y: { stacked: true, grid: { display: false }, border: { display: false }, ticks: { color: '#64748b', font: { weight: 'bold' as const } } },
    },
  };

  // A card shows only when the user holds the module (this also covers the clinic's
  // plan) AND the RPC returned that group (the server withholds groups it refuses).
  readonly agenda = computed(() => this.perm.hasModule(MODULE.AGENDA) && !!this.data().schedule);
  readonly patients = computed(() => this.perm.hasModule(MODULE.PACIENTES));
  readonly finance = computed(() => this.perm.hasModule(MODULE.FINANCEIRO));

  readonly smallCards = computed(() =>
    (this.agenda() && !!this.data().today_extras) ||
    (this.patients() && this.data().new_patients_today !== undefined) || null);

  readonly lowerCards = computed(() =>
    (this.finance() && !!this.data().billing) ||
    (this.agenda() && (!!this.data().doctor_load || !!this.data().waitlist)));

  readonly anything = computed(() =>
    this.agenda() || (this.patients() && this.data().new_patients_today !== undefined) ||
    (this.finance() && !!this.data().billing));
}
